<template>
    <div class="card">
        <DataTable :value="assessments" :resizableColumns="true" ref="dt" data-key="id" v-model:filters="state.filters" :rowHover="true" :loading="loading" responsiveLayout="scroll">
            <template #header>
                <div class="flex justify-content-between flex-column sm:flex-row">
                    <Button type="button" icon="pi pi-filter-slash" label="Clear" class="p-button-outlined mb-2" @click="clearFilters()" />
                    <div>
                        <span>
                            <label for="filter">Default Only:</label>
                            <Dropdown class="mx-2" v-model="params.default" :options="[true, 'false']" placeholder="Select Option" showClear />
                        </span>
                        <span class="p-input-icon-left mb-2">
                            <i :class="['pi', { 'pi-spinner pi-spin': state.filters['global'].loading }, { 'pi-search': !state.filters['global'].loading }]" />
                            <InputText maxlength = "255" v-model="state.filters['global'].value" placeholder="Keyword Search" style="width: 100%" @input="applyFilter" />
                        </span>
                    </div>
                </div>
            </template>
            <template #empty>No assessments available.</template>
            <template #loading>Loading Assessments. Please wait.</template>
            <Column field="id" header="ID" style="max-width: 4rem" />
            <Column field="name" header="NAME" style="width: 8rem" />
            <Column field="code" header="CODE" style="width: 8rem" />
            <Column field="description" header="DESCRIPTION" style="max-width: 14rem" />
            <Column field="default" header="DEFAULT" style="width: 6rem">
                <template #body="slotProps">
                    <Tag :severity="slotProps.data.default == 1 ? '' : 'danger'" :value="slotProps.data.default == 1 ? 'True' : 'False'" rounded />
                </template>
            </Column>
            <Column field="created_at" header="CREATED AT" style="width: 8rem">
                <template #body="slotProps">{{ formatDate(slotProps.data.created_at) }}</template>
            </Column>
            <Column field="actions" header="ACTIONS" style="width: 16rem">
                <template #body="slotProps">
                    <Button icon="pi pi-eye" class="p-button-rounded p-button-info mr-2" @click="operateAssessment(slotProps.data, 'view')" v-tooltip="'View Assessment Details'" />
                    <Button icon="pi pi-link" class="p-button-rounded p-button-warning mr-2" @click="operateAssessment(slotProps.data, 'configure')" v-tooltip="'Edit Steps and Links'" />
                    <Button icon="pi pi-pencil" class="p-button-rounded p-button-success mr-2" @click="operateAssessment(slotProps.data, 'edit')" v-tooltip="'Edit Assessment'" />
                    <Button icon="pi pi-trash" class="p-button-rounded p-button-danger mr-2" @click="confirmRequest(slotProps.data, assessmentStore.deleteAssessment)" v-tooltip="'Delete Assessment'" />
                    <Button v-if="slotProps.data.default != 1" icon="pi pi-check" class="p-button-rounded p-button-help" @click="confirmRequest(slotProps.data, assessmentStore.setAssessmentDefault, 'default')" v-tooltip="'Set Assessment Default'" />
                </template>
            </Column>
        </DataTable>
        <!-- Assessment edit dialog -->
        <assessment-form v-if="editDialog" @hide-dialog="editDialog = false" :header="'Update assessment'" :mode="'edit'" />
        <!-- Assessment details view dialog -->
        <assessment-details v-if="detailsDialog" @hide-dialog="detailsDialog = false" />
    </div>
</template>

<script setup>
import AssessmentForm from './AssessmentForm.vue';
import AssessmentDetails from './AssessmentDetails.vue';
import { ref, reactive, computed, watch, onMounted, inject } from 'vue';
import { useRouter } from 'vue-router';
import { FilterMatchMode } from 'primevue/api';
import { formatDate } from '@/helpers/date-formatter';
import { useConfirmComposable } from '@/composables/ConfirmComposable';
import { storeToRefs } from 'pinia';

const router = useRouter();
const assessmentStore = inject('assessmentStore');
const { confirmRequest } = useConfirmComposable();

const createInitialState = () => {
    return reactive({
        filters: {
            global: {
                value: null,
                matchMode: FilterMatchMode.CONTAINS,
                loading: false,
            },
        },
    });
};

const state = createInitialState();
const { assessments, loading, params } = storeToRefs(assessmentStore);

const editDialog = ref(false);
const detailsDialog = ref(false);

// to keep track of changes in filter for default only mode
const defaultOnly = computed(() => {
    return params?.default;
});

watch(defaultOnly, async () => {
    await loadAssessments();
});

onMounted(async () => {
    await loadAssessments();
});

const loadAssessments = async () => {
    await assessmentStore.fetchAllAssessments();
};

const operateAssessment = async (assessment, mode) => {
    // console.log(`Performing ${mode} operation on assessment ${id}`);
    switch (mode) {
        case 'edit':
            assessmentStore.editAssessment(assessment.id);
            editDialog.value = true;
            break;

        case 'view':
            assessmentStore.getAssessmentBlocks(assessment);
            detailsDialog.value = true;
            break;

        case 'configure':
            // push to steps and links configuration page
            return router.push({ name: 'AssessmentStepsAndLinksConfig', params: { id: assessment.id } });
    }
};

function applyFilter() {
    // Set the loading flag to true
    state.filters.global.loading = true;
    setTimeout(async () => {
        // Set the loading flag back to false
        await loadAssessments();
        state.filters.global.loading = false;
    }, 300);
}
function clearFilters() {
    assessmentStore.initParams();
    loadAssessments();
}
</script>
